import { PropsWithChildren, ReactNode } from "react";

import { Stack, Typography, useTheme } from "@mui/material";

/**
 * One labelled block inside a drawer section: an icon, a heading, and whatever
 * the block states.
 *
 * Shared by the allergen bands and the diet block so both read as headings
 * rather than captions, and so their icons sit on the same 20px baseline. The
 * icons come from the caller because their weights only match when each one is
 * asked for at the size this box gives it.
 */
/**
 * Every band icon renders at this, which is the `labelRMedium` beside it. One
 * constant because "the same size" has to survive the next icon added: the
 * mismatch that started this was three glyphs each asked for at their own size,
 * one of them a MUI default of 24.
 */
export const BAND_ICON_SIZE = 14;

/** A band nested inside another, scoped by the heading above it. */
export const DrawerSubBand = ({
  label,
  children,
}: PropsWithChildren<{ label: string }>) => {
  const {
    palette: { oscar },
  } = useTheme();

  // Not text.secondary: this theme maps it to electric blue, which renders
  // louder than the band heading above.
  return (
    <Stack gap={0.5} width="100%">
      <Typography variant="labelS" color={oscar.light.content.content1}>
        {label}
      </Typography>
      {children}
    </Stack>
  );
};

export const DrawerBand = ({
  label,
  icon,
  children,
}: PropsWithChildren<{ label: string; icon: ReactNode }>) => (
  <Stack gap={1} width="100%">
    <Stack alignItems="center" direction="row" gap={1}>
      {/* Box is the label's line box, so glyphs of different shapes still
          centre on the same baseline. */}
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ flexShrink: 0, height: 19, width: BAND_ICON_SIZE }}
      >
        {icon}
      </Stack>
      <Typography variant="labelRMedium">{label}</Typography>
    </Stack>
    {children}
  </Stack>
);
